import type { FC, FormEvent, ReactNode } from 'react'
import type { ShellData } from '@/types'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { ArrowUp, Bot, MessageCircle, RotateCcw, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { api } from '@/lib/api-client'
import { useI18n } from '@/lib/i18n'
import { whatsappLink } from '@/lib/phone'
import { CmsText } from '@/components/store/cms-text'

interface ChatMessage {
    role: 'user' | 'assistant'
    content: string
}

const STORAGE_KEY = 'np-assistant-v1'

export const OPEN_ASSISTANT_EVENT = 'np:open-assistant'

export const openAssistant = (): void => {
    window.dispatchEvent(new Event(OPEN_ASSISTANT_EVENT))
}

const safeHref = (href: string): string | null => {
    if (href.startsWith('/') && !href.startsWith('//')) return href
    if (/^https:\/\/(wa\.me|api\.whatsapp\.com)\//.test(href)) return href

    return null
}

const renderInline = (text: string, keyPrefix: string): Array<ReactNode> => {
    const nodes: Array<ReactNode> = []
    const pattern = /\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*/g
    let last = 0
    let index = 0
    let match: RegExpExecArray | null = pattern.exec(text)

    while (match) {
        if (match.index > last) nodes.push(text.slice(last, match.index))

        const key = `${keyPrefix}-${index}`

        if (match[1] !== undefined) {
            const href = safeHref(match[2])

            if (href && href.startsWith('/')) nodes.push(<Link key={key} href={href} className='font-medium text-cyan-700 underline decoration-cyan-300 underline-offset-2 hover:text-cyan-800'>{match[1]}</Link>)
            else if (href) nodes.push(<a key={key} href={href} target='_blank' rel='noopener noreferrer' className='font-medium text-cyan-700 underline decoration-cyan-300 underline-offset-2 hover:text-cyan-800'>{match[1]}</a>)
            else nodes.push(match[1])
        }
        else if (match[3] !== undefined) {
            nodes.push(<strong key={key} className='font-semibold text-navy-950'>{match[3]}</strong>)
        }

        last = pattern.lastIndex
        index += 1
        match = pattern.exec(text)
    }

    if (last < text.length) nodes.push(text.slice(last))

    return nodes
}

const MessageBody: FC<{ content: string }> = ({ content }): ReactNode => {
    const blocks: Array<ReactNode> = []
    let bullets: Array<string> = []

    const flush = (key: string): void => {
        if (bullets.length === 0) return

        blocks.push(
            <ul key={key} className='flex list-disc flex-col gap-1 ps-5'>
                {bullets.map((item, index) => <li key={index}>{renderInline(item, `${key}-${index}`)}</li>)}
            </ul>
        )
        bullets = []
    }

    content.split('\n').forEach((line, index) => {
        const trimmed = line.trim()

        if (/^[-*•]\s+/.test(trimmed)) {
            bullets.push(trimmed.replace(/^[-*•]\s+/, ''))

            return
        }

        flush(`list-${index}`)

        if (trimmed) blocks.push(<p key={`p-${index}`}>{renderInline(trimmed, `p-${index}`)}</p>)
    })

    flush('list-end')

    return <div className='flex flex-col gap-2'>{blocks}</div>
}

interface AssistantWidgetProps {
    shell: ShellData
}

export const AssistantWidget: FC<AssistantWidgetProps> = ({ shell }): ReactNode => {
    const { tx, txn, locale } = useI18n()
    const [open, setOpen] = useState(false)
    const [messages, setMessages] = useState<Array<ChatMessage>>([])
    const [input, setInput] = useState('')
    const [sending, setSending] = useState(false)
    const [loaded, setLoaded] = useState(false)
    const listRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLTextAreaElement>(null)

    const { settings } = shell

    useEffect(() => {
        try {
            const raw = window.sessionStorage.getItem(STORAGE_KEY)

            if (raw) {
                const parsed = JSON.parse(raw)

                if (Array.isArray(parsed)) {
                    setMessages(parsed.filter((entry): entry is ChatMessage => entry && (entry.role === 'user' || entry.role === 'assistant') && typeof entry.content === 'string').slice(-30))
                }
            }
        }
        catch {
            setMessages([])
        }

        setLoaded(true)
    }, [])

    useEffect(() => {
        if (!loaded) return

        try {
            window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-30)))
        }
        catch {
            /* private browsing can block storage; the chat still works */
        }
    }, [messages, loaded])

    useEffect(() => {
        if (!open) return

        listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' })
    }, [messages, open, sending])

    useEffect(() => {
        if (open) window.setTimeout(() => inputRef.current?.focus(), 80)
    }, [open])

    useEffect(() => {
        const onOpen = (): void => setOpen(true)

        window.addEventListener(OPEN_ASSISTANT_EVENT, onOpen)

        return () => window.removeEventListener(OPEN_ASSISTANT_EVENT, onOpen)
    }, [])

    useEffect(() => {
        if (!open) return

        const onKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setOpen(false)
        }

        document.addEventListener('keydown', onKey)

        return () => document.removeEventListener('keydown', onKey)
    }, [open])

    if (!settings.assistantEnabled) return null

    const suggestions = locale === 'en'
        ? ['How do wholesale prices work?', 'What offers are running now?', 'How much is shipping to Alexandria?', 'Do you have food containers?']
        : ['إزاي أسعار الجملة بتشتغل؟', 'إيه العروض المتاحة دلوقتي؟', 'الشحن لإسكندرية بكام؟', 'عندكم علب حفظ طعام؟']

    const send = async (text: string): Promise<void> => {
        const content = text.trim().slice(0, 600)

        if (!content || sending) return

        const next: Array<ChatMessage> = [...messages, { role: 'user', content }]

        setMessages(next)
        setInput('')
        setSending(true)

        try {
            const data = await api<{ reply: string }>('/api/assistant', { body: { messages: next.slice(-10) }, locale })

            setMessages(current => [...current, { role: 'assistant', content: data.reply }])
        }
        catch (error) {
            const fallback = settings.whatsapp
                ? tx('تعذّر الرد الآن. تقدر تكلمنا مباشرة على واتساب.', 'I could not answer right now. You can reach our team on WhatsApp.')
                : tx('تعذّر الرد الآن، حاول مرة أخرى بعد قليل.', 'I could not answer right now. Please try again shortly.')

            setMessages(current => [...current, { role: 'assistant', content: error instanceof Error && error.message ? error.message : fallback }])
        }
        finally {
            setSending(false)
        }
    }

    const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()
        void send(input)
    }

    const greeting = txn(
        'أهلاً بك في {store}. أنا المساعد الذكي، أقدر أساعدك في المنتجات وأسعار الجملة والقطاعي والعروض والشحن.',
        'Welcome to {store}. I am the store assistant and can help with products, wholesale and retail prices, offers and shipping.',
        { store: <CmsText table='site_settings' id={settings.id} field={settings.storeName} className='font-semibold text-navy-950' /> }
    )

    return (
        <>
            <button
                type='button'
                onClick={() => setOpen(value => !value)}
                className={cn(
                    'fixed bottom-[max(1rem,env(safe-area-inset-bottom))] end-4 z-[60] flex h-14 items-center gap-2 rounded-full bg-navy-900 ps-4 pe-5 text-sm font-semibold text-white shadow-lift ring-4 ring-white/70 transition-all duration-300 hover:bg-navy-800 active:scale-95 sm:end-6',
                    open && 'pointer-events-none scale-90 opacity-0'
                )}
                aria-label={tx('افتح المساعد الذكي', 'Open the store assistant')}
                aria-expanded={open}
            >
                <span className='relative flex size-8 items-center justify-center rounded-full bg-cyan-400 text-navy-950'>
                    <Bot className='size-4.5' aria-hidden='true' />
                    <span className='absolute -end-0.5 -top-0.5 size-2.5 rounded-full bg-emerald-400 ring-2 ring-navy-900' aria-hidden='true' />
                </span>
                <span className='hidden sm:inline'>{tx('اسأل المساعد', 'Ask the assistant')}</span>
            </button>

            {open && (
                <div
                    className='fixed inset-0 z-[75] flex flex-col bg-white animate-in fade-in slide-in-from-bottom-4 duration-300 sm:inset-auto sm:bottom-6 sm:end-6 sm:h-[min(640px,calc(100vh-3rem))] sm:w-[400px] sm:overflow-hidden sm:rounded-3xl sm:shadow-lift sm:ring-1 sm:ring-navy-100'
                    role='dialog'
                    aria-label={tx('المساعد الذكي', 'Store assistant')}
                >
                    <div className='flex items-center gap-3 bg-navy-950 px-4 py-3.5 pt-[max(0.875rem,env(safe-area-inset-top))] text-white'>
                        <span className='flex size-10 items-center justify-center rounded-2xl bg-cyan-400 text-navy-950'>
                            <Bot className='size-5' aria-hidden='true' />
                        </span>
                        <div className='flex flex-1 flex-col'>
                            <span className='text-sm font-semibold'>{tx('المساعد الذكي', 'Store assistant')}</span>
                            <span className='text-xs text-white/60'>{tx('إجابات من بيانات المتجر الفعلية', 'Answers from live store data')}</span>
                        </div>

                        {messages.length > 0 && (
                            <button type='button' onClick={() => setMessages([])} className='inline-flex size-9 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-white' aria-label={tx('محادثة جديدة', 'New conversation')}>
                                <RotateCcw className='size-4' />
                            </button>
                        )}

                        <button type='button' onClick={() => setOpen(false)} className='inline-flex size-9 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-white' aria-label={tx('إغلاق', 'Close')}>
                            <X className='size-5' />
                        </button>
                    </div>

                    <div ref={listRef} className='flex-1 overflow-y-auto overscroll-contain bg-surface px-4 py-5'>
                        <div className='flex flex-col gap-3'>
                            <div className='max-w-[88%] self-start rounded-3xl rounded-ss-md bg-white px-4 py-3 text-sm leading-relaxed text-navy-800 shadow-soft'>
                                {greeting}
                            </div>

                            {messages.length === 0 && (
                                <div className='flex flex-wrap gap-2 pt-1'>
                                    {suggestions.map(suggestion => (
                                        <button key={suggestion} type='button' onClick={() => void send(suggestion)} className='rounded-full border border-navy-100 bg-white px-3.5 py-2 text-xs font-medium text-navy-800 transition-colors hover:border-cyan-300 hover:bg-cyan-50'>
                                            {suggestion}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {messages.map((message, index) => (
                                <div
                                    key={index}
                                    className={cn(
                                        'max-w-[88%] rounded-3xl px-4 py-3 text-sm leading-relaxed',
                                        message.role === 'user'
                                            ? 'self-end rounded-se-md bg-navy-900 text-white'
                                            : 'self-start rounded-ss-md bg-white text-navy-800 shadow-soft'
                                    )}
                                >
                                    {message.role === 'user' ? <p className='whitespace-pre-line'>{message.content}</p> : <MessageBody content={message.content} />}
                                </div>
                            ))}

                            {sending && (
                                <div className='flex items-center gap-1.5 self-start rounded-3xl rounded-ss-md bg-white px-4 py-4 shadow-soft' aria-label={tx('جاري الكتابة', 'Typing')}>
                                    <span className='size-2 animate-bounce rounded-full bg-navy-300 [animation-delay:-0.3s]' />
                                    <span className='size-2 animate-bounce rounded-full bg-navy-300 [animation-delay:-0.15s]' />
                                    <span className='size-2 animate-bounce rounded-full bg-navy-300' />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className='border-t border-border bg-white p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]'>
                        <form onSubmit={onSubmit} className='flex items-end gap-2'>
                            <label htmlFor='assistant-input' className='sr-only'>{tx('اكتب سؤالك', 'Type your question')}</label>
                            <textarea
                                ref={inputRef}
                                id='assistant-input'
                                rows={1}
                                value={input}
                                maxLength={600}
                                onChange={event => setInput(event.target.value)}
                                onKeyDown={event => {
                                    if (event.key === 'Enter' && !event.shiftKey) {
                                        event.preventDefault()
                                        void send(input)
                                    }
                                }}
                                placeholder={tx('اكتب سؤالك هنا...', 'Type your question...')}
                                className='max-h-32 min-h-11 flex-1 resize-none rounded-2xl border border-navy-100 bg-navy-50/60 px-4 py-2.5 text-sm text-navy-950 placeholder:text-navy-400 focus:border-cyan-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-cyan-400/20'
                            />
                            <button type='submit' disabled={!input.trim() || sending} className='inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-navy-900 text-white transition-all hover:bg-navy-800 disabled:opacity-40' aria-label={tx('إرسال', 'Send')}>
                                <ArrowUp className='size-5' />
                            </button>
                        </form>

                        {settings.whatsapp && (
                            <a href={whatsappLink(settings.whatsapp)} target='_blank' rel='noopener noreferrer' className='mt-2 flex items-center justify-center gap-2 rounded-xl py-1.5 text-xs font-medium text-navy-600 transition-colors hover:text-[#1a9049]'>
                                <MessageCircle className='size-4 text-[#1fa855]' aria-hidden='true' />
                                {tx('تفضّل التحدث مع شخص؟ راسلنا على واتساب', 'Prefer a person? Message us on WhatsApp')}
                            </a>
                        )}
                    </div>
                </div>
            )}
        </>
    )
}
